import { useState } from 'react';

import { loadFromLocalStorage, saveInLocalStorage } from '../../common/onboarding/handleLocalStorage';
import { type BookingOverviewColumn, defaultBookingOverviewColumns } from './bookingOverviewColumns';

const storageKey = 'bookings.overview.visibleColumns';
const orderStorageKey = 'bookings.overview.columnOrder';

function normalizeColumnOrder(stored: unknown): BookingOverviewColumn[] {
    const order = Array.isArray(stored)
        ? stored.filter((column): column is BookingOverviewColumn => defaultBookingOverviewColumns.includes(column))
        : [];
    return [...new Set<BookingOverviewColumn>(['resourceName', ...order, ...defaultBookingOverviewColumns])];
}

function loadVisibleColumns(): BookingOverviewColumn[] {
    const storedColumns: unknown = loadFromLocalStorage(storageKey);
    if (!Array.isArray(storedColumns) || !storedColumns.every(column => typeof column === 'string')) {
        return defaultBookingOverviewColumns;
    }

    return defaultBookingOverviewColumns.filter(column => column === 'resourceName' || storedColumns.includes(column));
}

export function useBookingOverviewColumns() {
    const [visibleColumnIds, setVisibleColumnIds] = useState(loadVisibleColumns);
    const [columnOrder, setColumnOrder] = useState(() => normalizeColumnOrder(loadFromLocalStorage(orderStorageKey)));

    const changeVisibleColumns = (columns: BookingOverviewColumn[]) => {
        setVisibleColumnIds(columns);
        saveInLocalStorage(storageKey, columns);
    };

    const changeColumnOrder = (columns: string[]) => {
        const order = normalizeColumnOrder(columns);
        setColumnOrder(order);
        saveInLocalStorage(orderStorageKey, order);
    };

    const resetColumns = () => {
        changeVisibleColumns(defaultBookingOverviewColumns);
        changeColumnOrder(defaultBookingOverviewColumns);
    };

    return {
        visibleColumns: columnOrder.filter(column => visibleColumnIds.includes(column)),
        columnOrder,
        changeVisibleColumns,
        changeColumnOrder,
        resetColumns,
    };
}
